import React, { useEffect, useMemo, useState } from "react";

import Dropzone from "../Common/Dropzone";

import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import InputLabel from "@mui/material/InputLabel";

import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";

import JobService from "../../service/job";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/actions";
import HistoryTable from "./HistoryTable";

const useStyles = makeStyles((theme) =>
  createStyles({
    gridItem: {
      minHeight: "300px",
    },
    paper: {
      height: "100%",
      padding: "10px 20px",
    },
    detailsHeader: {
      fontWeight: "normal",
      "&::after": {
        content: "''",
        display: "block",
        width: "40px",
        height: "3px",
        background: "#EC5143",
        margin: "5px 0 0",
        borderRadius: "5px",
      },
    },
  })
);

const columns = [
  {
    id: "approve_status",
    label: "Status",
    width: 100,
  },
  {
    id: "description",
    label: "Description",
    width: 200,
  },
  {
    id: "customer_description",
    label: "Customer Comments",
    width: 200,
  },
  {
    id: "user",
    label: "Updated By",
    width: 150,
  },
  { id: "files", label: "Files", width: 300 },
  {
    id: "download",
    label: "Download",
    width: 100,
    icon: "Download",
  },
];

const Artwork = ({
  job_id,
  status,
  setStatus,
  defaultArtworkFiles,
  defaultArtworkDescription,
  defaultArtworkComments,
  setValue,
  role,
  categoryImageFileStep,
  isSupplierJob,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [description, setDescription] = useState(defaultArtworkDescription);
  const [comments, setComments] = useState(defaultArtworkComments);
  const [imageFiles, setImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    setHistoryLoading(true);

    const Job = new JobService();
    Job.getHistory(job_id, "Artwork")
      .then((response) => {
        if (response.success) setHistory(response.data);

        setHistoryLoading(false);
      })
      .catch(() => {
        setHistoryLoading(false);
      });
  }, [job_id]);

  const handleArtworkSubmit = async () => {
    setLoading(true);
    const Job = new JobService();

    const formData = new FormData();
    formData.append("ArtworkDescription", description);
    if (role === 3 || (isSupplierJob && (role === 2 || role === 1))) formData.append("ArtworkComments", comments);
    if ((!isSupplierJob && (role === 2 || role === 1)) || (isSupplierJob && role === 4)) formData.append("Status", 5);
    imageFiles.forEach((file) => formData.append("ArtworkFile", file));
    formData.append("addStatus", "Artwork");

    Job.updateJob(job_id, formData).then((response) => {
      if (response.success) {
        if ((!isSupplierJob && (role === 2 || role === 1)) || (isSupplierJob && role === 4)) setStatus(5);
        setValue(0);
        dispatch(
          showToast({
            type: "success",
            message: "Artwork submitted successfully!",
          })
        );
      }

      setLoading(false);
    });
  };

  const handleApproveRejectArtwork = (type) => {
    setLoading(true);

    // If type is 1 that is Approve order then set status according to
    // if Category Image file is enabled from DB then set status to 6 (Image File in process) else set status to 8 (In Production)
    // Else set status to 4 (Artwork in process)
    const statusValue = type === 1 ? (categoryImageFileStep == 1 ? 6 : 8) : 4;

    const Job = new JobService();

    const formData = new FormData();
    formData.append("ArtworkComments", comments);
    formData.append("Status", statusValue);
    formData.append("addStatus", "Artwork");

    Job.updateJob(job_id, formData).then((response) => {
      if (response.success) {
        setStatus(statusValue);
        setValue(0);
        dispatch(
          showToast({
            type: "success",
            message: type === 1 ? "Artwork approved successfully!" : "Artwork rejected successfully!",
          })
        );
      }
      setLoading(false);
    });
  };

  const showSubmitArtwork = useMemo(() => {
    if (role === 1 || role === 2) return !isSupplierJob;
    if (role === 3) return status != 5;
    else if (role === 4) return isSupplierJob;

    return false;
  }, [isSupplierJob, role, status]);

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Backdrop sx={{ color: "red", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <Box
          sx={{
            overflow: "hidden",
          }}
        >
          <Paper className={classes.paper}>
            <Grid container spacing={2} justifyContent="center" height="100%">
              <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className={classes.gridItem}>
                <h3 className={classes.detailsHeader}>
                  <b> Upload Artwork files </b>
                </h3>

                <Dropzone handleUploadFile={(files) => setImageFiles(files)} />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className={classes.gridItem}>
                {role === 3 || (isSupplierJob && (role === 2 || role === 1)) ? (
                  <>
                    <InputLabel sx={{ mt: 1 }} className="inputFormCustomLabel">
                      Comments
                    </InputLabel>
                    <TextField
                      margin="normal"
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      fullWidth
                      variant="filled"
                      placeholder="Enter Comments Here"
                      hiddenLabel
                      sx={{ mt: 1 }}
                      multiline
                      rows={5}
                    />

                    {status == 5 && (
                      <>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleApproveRejectArtwork(1)}
                          sx={{ mt: 1.5 }}
                          style={{ padding: "5px 20px" }}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleApproveRejectArtwork(0)}
                          sx={{ mt: 1.5 }}
                          style={{ padding: "5px 20px", marginLeft: "15px" }}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <InputLabel sx={{ mt: 1 }} className="inputFormCustomLabel">
                      Description
                    </InputLabel>
                    <TextField
                      margin="normal"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      fullWidth
                      variant="filled"
                      placeholder="More Information or Make Changes"
                      hiddenLabel
                      sx={{ mt: 1 }}
                      multiline
                      rows={5}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>
      {showSubmitArtwork && (
        <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
          <Box sx={{ flex: "1 1 auto" }} />
          <Button
            variant="contained"
            size="small"
            onClick={handleArtworkSubmit}
            style={{ backgroundColor: "#1b5e20", padding: "5px 20px" }}
          >
            Submit Artwork
          </Button>
        </Box>
      )}
      <HistoryTable columns={columns} history={history} historyLoading={historyLoading} />
    </>
  );
};

export default Artwork;
